import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import LoginPage from '../../features/auth/pages/LoginPage';
import ForgotPasswordPage from '../../features/auth/pages/ForgotPasswordPage';
import McpAuthorizationPage from '../../features/auth/pages/McpAuthorizationPage';
import ExternalCredentialSetupPage from '../../features/integrations/pages/ExternalCredentialSetupPage';
import ConnectorSetupPage from '../../features/integrations/pages/ConnectorSetupPage';
import SenderCallbackPage from '../../features/personSenders/SenderCallbackPage';
import AcceptTeamInvitationPage from '../../features/auth/pages/AcceptTeamInvitationPage';
import VerifyEmailPage from '../../features/auth/pages/VerifyEmailPage';
import AppShell from '../shell/AppShell';
import VersionChecker from 'shared/components/VersionChecker';
import BackendHealthCheck from 'shared/components/BackendHealthCheck';
import { ProtectedRoute } from './ProtectedRoute';
import { PublicRoute } from './guards';
import {
  APP_SHELL_ROUTE_PREFIX,
  LEGACY_ADMIN_SHELL_ROUTE_PREFIX,
} from './routeManifest';

const VIEW_HARNESS_ENABLED = !import.meta.env.PROD;
const DEV_FLOWCHART_EDITOR_ENABLED = !import.meta.env.PROD;
const ViewHarnessPage = VIEW_HARNESS_ENABLED ? lazy(() => import('../../features/views/pages/ViewHarnessPage')) : null;
const FlowchartEditorPage = DEV_FLOWCHART_EDITOR_ENABLED ? lazy(() => import('../../features/devtools/flowchart-editor').then((module) => ({ default: module.FlowchartEditorPage }))) : null;

function LegacyAdminRedirect() {
  const params = useParams();
  const splat = params['*'] || '';
  return <Navigate to={`${APP_SHELL_ROUTE_PREFIX}${splat ? `/${splat}` : ''}`} replace />;
}

export function AppRouter() {
  const adminShellElement = (
    <ProtectedRoute>
      <AppShell />
    </ProtectedRoute>
  );

  return (
    <>
      <VersionChecker />
      <BackendHealthCheck />
      <div className="min-h-screen">
        <Routes>
          <Route
            path="/"
            element={
              <PublicRoute>
                <Navigate to="/login" replace />
              </PublicRoute>
            }
          />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <Navigate to="/forgot-password" replace />
              </PublicRoute>
            }
          />
          <Route path="/auth/callback" element={<Navigate to="/login" replace />} />
          <Route path="/app/mcp/authorize" element={<McpAuthorizationPage />} />
          <Route
            path="/app/integration-credentials/:requestId"
            element={
              <ProtectedRoute>
                <ExternalCredentialSetupPage />
              </ProtectedRoute>
            }
          />
          {/* Google returns a sender sign-in here; the page posts it back as the signed-in member. */}
          <Route
            path="/app/senders/callback"
            element={
              <ProtectedRoute>
                <SenderCallbackPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accept-invitation"
            element={<AcceptTeamInvitationPage />}
          />
          {/* Link for IT: someone without a LineSide account installs a connector. */}
          <Route path="/connect" element={<ConnectorSetupPage />} />
          {VIEW_HARNESS_ENABLED ? (
            <Route path="/view-harness" element={<Suspense fallback={<p>Opening harness…</p>}><ViewHarnessPage /></Suspense>} />
          ) : null}
          {DEV_FLOWCHART_EDITOR_ENABLED ? (
            <Route
              path="/dev/flowchart-editor"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<p>Opening editor…</p>}><FlowchartEditorPage /></Suspense>
                </ProtectedRoute>
              }
            />
          ) : null}
          <Route path={`${APP_SHELL_ROUTE_PREFIX}/*`} element={adminShellElement} />
          <Route path={LEGACY_ADMIN_SHELL_ROUTE_PREFIX} element={<LegacyAdminRedirect />} />
          <Route path={`${LEGACY_ADMIN_SHELL_ROUTE_PREFIX}/*`} element={<LegacyAdminRedirect />} />
          <Route
            path="/floor"
            element={
              <ProtectedRoute>
                <Navigate to="/app/views/name/Production%20Floor" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quality"
            element={
              <ProtectedRoute>
                <Navigate to="/app/views/name/Quality%20Inspections" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/verify-email"
            element={
              <PublicRoute>
                <VerifyEmailPage />
              </PublicRoute>
            }
          />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    </>
  );
}
